import { gql } from '@apollo/client/core';

/**
 * The school notice board — a school talking to its own students.
 *
 * Deliberately separate from `announcements.ts`, which is the PLATFORM
 * addressing tenant schools and whose audience only ever resolves to a School.
 * The two share nothing but the word, so mixing them in one document would
 * make the operation names ambiguous on both sides.
 *
 * Written by hand rather than generated, matching the pattern #223 set for the
 * other announcement surface.
 */

/** Fields every notice surface needs. */
const NOTICE_FIELDS = gql`
  fragment SchoolNoticeFields on SchoolAnnouncement {
    id
    title
    body
    severity
    requires_acknowledgement
    pinned
    audience_label
    published_at
    expires_at
    read_at
    acknowledged_at
    createdBy {
      id
      name
    }
  }
`;

/** The student's board: everything they can see, read or not. */
export const STUDENT_ANNOUNCEMENTS = gql`
  query StudentAnnouncements {
    studentAnnouncements {
      ...SchoolNoticeFields
    }
  }
  ${NOTICE_FIELDS}
`;

/** What the portal blocks on. A non-empty result keeps the gate up. */
export const PENDING_ANNOUNCEMENT_ACKNOWLEDGEMENTS = gql`
  query PendingAnnouncementAcknowledgements {
    pendingAnnouncementAcknowledgements {
      ...SchoolNoticeFields
    }
  }
  ${NOTICE_FIELDS}
`;

export const MARK_SCHOOL_ANNOUNCEMENT_READ = gql`
  mutation MarkSchoolAnnouncementRead($id: ID!) {
    markSchoolAnnouncementRead(id: $id) {
      id
      read_at
    }
  }
`;

export const ACKNOWLEDGE_SCHOOL_ANNOUNCEMENT = gql`
  mutation AcknowledgeSchoolAnnouncement($id: ID!) {
    acknowledgeSchoolAnnouncement(id: $id) {
      id
      read_at
      acknowledged_at
    }
  }
`;

/** Admin list, with reach statistics. */
export const SCHOOL_ANNOUNCEMENTS = gql`
  query SchoolAnnouncements($include_inactive: Boolean) {
    schoolAnnouncements(include_inactive: $include_inactive) {
      ...SchoolNoticeFields
      is_active
      audience_type
      recipient_count
      read_count
      acknowledged_count
      audiences {
        id
        audience_type
        audience_id
        name
      }
    }
  }
  ${NOTICE_FIELDS}
`;

export const CREATE_SCHOOL_ANNOUNCEMENT = gql`
  mutation CreateSchoolAnnouncement($input: CreateSchoolAnnouncementInput!) {
    createSchoolAnnouncement(input: $input) {
      id
      title
    }
  }
`;

export const UPDATE_SCHOOL_ANNOUNCEMENT = gql`
  mutation UpdateSchoolAnnouncement($input: UpdateSchoolAnnouncementInput!) {
    updateSchoolAnnouncement(input: $input) {
      id
      title
      is_active
    }
  }
`;

/**
 * Targeting options for the editor.
 *
 * Arms carry their level, so the picker can offer "JSS 2 (whole level)" and
 * "JSS 2 Gold" from one round trip instead of two.
 */
export const ANNOUNCEMENT_AUDIENCE_OPTIONS = gql`
  query AnnouncementAudienceOptions {
    getSchool {
      id
      classroomSections {
        id
        name
        classroom {
          id
          name
        }
      }
    }
  }
`;

export interface AudienceOptionSection {
  id: string;
  name: string | null;
  classroom?: { id: string; name: string } | null;
}

